import type { Definition } from 'typescript-json-schema';
import { SchemaTag } from '@sap/ux-specification-types';
import type { DecoratorStep, DecoratorContext } from '../interfaces';
import { compareUI5Versions } from '../../../v4/sync-rules/utils';
import { metadataKeys } from '../decorators';
import { resolveEnumFromProperty, applyFilteredEnumToProperty } from './enum-utils';

/**
 * Validity step - hides properties and filters enum values based on UI5 version requirements.
 * Runs first in the pipeline.
 */
export class Validity implements DecoratorStep {
    readonly name = 'validity';

    /**
     * Hides properties or filters enum values based on UI5 version requirements.
     *
     * @param target - The class prototype containing decorator metadata
     * @param definition - The schema definition to modify
     * @param context - Runtime context providing the minimum UI5 version
     */
    init(target: object, definition: Definition, context: DecoratorContext): void {
        const minUI5Version = context.app?.getMinUI5Version?.();
        if (!definition?.properties || !minUI5Version) {
            return;
        }

        for (const propertyName in definition.properties) {
            const property = definition.properties[propertyName] as Definition;
            const validityInfo = Reflect.getMetadata(metadataKeys.validity, target, propertyName);

            // Check if property has a 'since' requirement that exceeds the app's minUI5Version
            if (validityInfo?.since && !compareUI5Versions(minUI5Version, validityInfo.since)) {
                property[SchemaTag.hidden] = true;
            }

            // Process enum validity configuration
            if (validityInfo?.enum) {
                const enumValues = resolveEnumFromProperty(property, context);
                if (enumValues) {
                    const filteredEnum = enumValues.filter((enumValue) => {
                        const enumValueStr = String(enumValue);
                        const condition = validityInfo.enum?.[enumValueStr];
                        if (!condition) {
                            return true; // Keep values without conditions
                        }

                        // Check UI5 version requirement
                        if (condition.since && minUI5Version) {
                            if (!compareUI5Versions(minUI5Version, condition.since)) {
                                return false;
                            }
                        }

                        // Check property dependency using dependsOn function
                        if (condition.dependsOn) {
                            if (!condition.dependsOn(context)) {
                                return false;
                            }
                        }

                        return true;
                    });

                    if (filteredEnum.length !== enumValues.length) {
                        applyFilteredEnumToProperty(property, filteredEnum, context);
                    }
                }
            }
        }
    }
}
